/**
 * Planning Service
 * Handles all API calls related to planning applications and development data
 */

import { apiRequest } from './api';
import config from '../config';

/**
 * Fetch planning applications for a location
 *
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {number} radius - Search radius in miles (default: 0.2)
 * @param {string} postcode - Optional postcode for the location
 * @returns {Promise<Array>} - Array of planning applications
 */
const getPlanningApplications = async (lat, lng, radius = 0.2, postcode = null) => {
  let url = `${config.apiUrl}/planning/applications?lat=${lat}&lon=${lng}&radius=${radius}`;

  // Add postcode to the URL if provided
  if (postcode) {
    url += `&postcode=${encodeURIComponent(postcode)}`;
  }

  // Create a cache key based on the location and postcode
  const parsedLat = typeof lat === 'number' ? lat : parseFloat(lat);
  const parsedLng = typeof location.lng === 'number' ? lng : parseFloat(lng);
  const cacheKey =
    lng && lat
      ? `planning_${parsedLat?.toFixed(4)}_${parsedLng?.toFixed(4)}_${radius}_${postcode || ''}`
      : 'planning_loading_key';

  // Planning data might change frequently, use a moderate cache time
  const cacheDuration = 6 * 60 * 60 * 1000; // 6 hours

  try {
    const response = await apiRequest(url, {}, cacheKey, cacheDuration);
    return {
      applications: response.data || [],
      isLoading: false,
      error: null,
    };
  } catch (error) {
    console.error('Error fetching planning applications:', error);
    return {
      applications: [],
      isLoading: false,
      error: 'Failed to fetch planning applications',
    };
  }
};

/**
 * Get development data for specific property or area
 *
 * @param {string} propertyId - ID of the property
 * @returns {Promise<Object>} - Development data
 */
const getDevelopmentData = async (propertyId) => {
  const url = `${config.apiUrl}/planning/development/${propertyId}`;

  // Create a cache key based on the property ID
  const cacheKey = `development_${propertyId}`;

  try {
    return await apiRequest(url, {}, cacheKey);
  } catch (error) {
    console.error('Error fetching development data:', error);
    throw error;
  }
};

/**
 * Calculate potential development profit based on property data
 *
 * @param {Object} propertyData - Property data including price, etc.
 * @returns {Object} - Development profit calculations
 */
const calculateDevelopmentProfit = (propertyData) => {
  // This is a placeholder for future functionality
  // Calculate potential development profitability based on property data
  return {
    potentialValue: 0,
    developmentCost: 0,
    profit: 0,
    roi: 0,
  };
};

export { getPlanningApplications, getDevelopmentData, calculateDevelopmentProfit };
